import { Button, Label, TableItem } from 'components/atoms';
import { FormInput, MagellanPopups } from 'components/molecules';
import { Table, TopBar } from 'components/organisms';
import { TopbarPage } from 'components/templates';
import { FormikHelpers } from 'formik';
import { useInfiniteTable } from 'hooks/useInfiniteTable';
import { useParamId } from 'hooks/useParamId';
import { useTranslation } from 'react-i18next';
import {
  PiEye,
  PiNavigationArrow,
  PiThumbsDown,
  PiThumbsUp,
  PiVideo,
} from 'react-icons/pi';
import { PopupActions } from 'reactjs-popup/dist/types';
import {
  useGetChannelVideosInfinite,
  usePostChannelVideoById,
} from 'services/channel';
import { IAddVideoById } from 'services/channel/types/query';
import { object, string } from 'yup';

const ChannelVideo = () => {
  const channelId = useParamId('channel');

  const getVideos = useGetChannelVideosInfinite(channelId);

  const addVideo = usePostChannelVideoById(channelId);

  const tableProps = useInfiniteTable(getVideos);

  const { t } = useTranslation();

  const i18nextPrefix = 'channel.video';

  const navigateToYtVideo = (videoId: string) => {
    const targetPath = `https://www.youtube.com/watch?v=${videoId}`;
    window.open(targetPath, '_blank');
  };

  const onAddVideo = async (
    values: IAddVideoById,
    formikHelpers: FormikHelpers<IAddVideoById>,
    actions: PopupActions | null
  ) =>
    await addVideo.mutateAsync(values, {
      onSuccess: () => {
        getVideos.refetch();
        actions?.close();
      },
    });

  return (
    <TopbarPage
      topbar={
        <TopBar
          size='compact'
          hideHeader
          topbarContent={
            <MagellanPopups.Form
              header={
                <>
                  <PiVideo /> {t('channel.popup.input.addByVideoId')}
                </>
              }
              validationSchema={object().shape({
                videoId: string().required(t('formik-error.required')),
              })}
              initialValues={{ videoId: '' }}
              onSubmit={onAddVideo}
              trigger={
                <Button type='submit'>
                  <Button.Text text={t('button.add')} />
                </Button>
              }>
              <FormInput.Text
                type='text'
                label={t(`channel.popup.input.byVideoId`)}
                placeholder='PUwfdhRc5jYswE'
                name={'videoId'}
              />
            </MagellanPopups.Form>
          }
        />
      }>
      <Table
        id={'ChannelVideo'}
        size='compact'
        {...tableProps}
        data={{
          nodes: tableProps.data.nodes.map(v => ({
            ...v,
            id: `${v.id}/player`,
          })),
        }}
        columns={[
          {
            label: `${t(`${i18nextPrefix}.table.videoTitle`)}`,
            renderCell: ({ videoTitle, videoImageLink }) => (
              <TableItem>
                <div className='flex flex-row items-center gap-4'>
                  <img src={videoImageLink} alt='' style={{ width: '80px' }} />
                  <TableItem.Text text={videoTitle} />
                </div>
              </TableItem>
            ),
            resize: false,
          },

          {
            label: '',
            renderCell: ({
              externalVideoId,
              dislikesCount,
              likesCount,
              views,
            }) => (
              <TableItem justifyPosition='end'>
                <div className='flex gap-2 items-center'>
                  <Label
                    title='Views'
                    size='compact'
                    withText
                    style={{ minWidth: 120 }}
                    color='blue'>
                    <PiEye /> {views.toLocaleString()}
                  </Label>
                  <Label
                    title='Likes'
                    size='compact'
                    withText
                    style={{ minWidth: 100 }}>
                    <PiThumbsUp /> {likesCount.toLocaleString()}
                  </Label>
                  {!!dislikesCount && (
                    <Label size='compact' withText style={{ minWidth: 100 }}>
                      <PiThumbsDown /> {dislikesCount.toLocaleString()}
                    </Label>
                  )}
                  <Button
                    title='Dislikes'
                    type='button'
                    isClear
                    onlyIcon
                    onClick={() => navigateToYtVideo(externalVideoId)}>
                    <Button.Icon
                      icon={<PiNavigationArrow transform='scale(-1,1)' />}
                    />
                  </Button>
                </div>
              </TableItem>
            ),
          },

          // {
          //   label: t(`${i18nextPrefix}.table.externalVideoId`),
          //   renderCell: ({ externalVideoId }) => (
          //     <TableItem>
          //       <TableItem.Text isSupplementary text={externalVideoId} />
          //     </TableItem>
          //   ),
          //   resize: true,
          // },
        ]}
        displayMode
      />
    </TopbarPage>
  );
};

export default ChannelVideo;
